<script setup lang="ts">
import { ref } from 'vue'
import { genFileId } from 'element-plus'
import type { UploadInstance, UploadProps, UploadRawFile, UploadFile, UploadFiles } from 'element-plus'
import { createBulkMiners, downloadSampleBulkMinersFile } from '@/services/rc.service'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import type { BulkMinersResult } from '@/types/rc/miners'
import { UploadFilled } from '@element-plus/icons-vue'
import { downloadFile } from '@/util'
import { NotifySuccess } from '@/helpers/core/notifications'

const queryClient = useQueryClient()

const open = defineModel<boolean>()
const props = defineProps<{ workOrderNumber: string }>()
const uploadInst = ref<UploadInstance>()
const fileToUpload = ref<File>()
const supportedFileMimeTypes = ['application/vnd.ms-excel', 'text/csv']
const supportefFileMimeTypesStr = 'application/vnd.ms-excel,text/csv'
const uploadErrors = ref<boolean>(false)

const handleExceed: UploadProps['onExceed'] = (files) => {
  uploadInst.value!.clearFiles()
  const file = files[0] as UploadRawFile
  file.uid = genFileId()
  uploadInst.value!.handleStart(file)
}

const { isPending, mutate } = useMutation({
  mutationFn: ({ workOrderNumber, csvFile }: { workOrderNumber: string; csvFile: File }) =>
    createBulkMiners(workOrderNumber, csvFile),
  onSuccess: (res: BulkMinersResult) => {
    if (res.success) {
      NotifySuccess('Successfully imported miners')
      queryClient.invalidateQueries({ queryKey: ['miners'] })
      open.value = false
    } else {
      uploadErrors.value = true
      downloadFileErrors(res.errors)
    }
  },
  onError: (e) => {
    ElMessage.error({ message: e.message })
  },
})

/**
 * Handles upload file added.
 *
 * @param uploadFile the file added.
 * @param uploadFiles the file list.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleChange: UploadProps['onChange'] = async (uploadFile: UploadFile, uploadFiles: UploadFiles) => {
  // Validate file type
  if (!supportedFileMimeTypes.includes(uploadFile.raw!.type)) {
    ElMessage.error(`Unsupported file type. File must be a CSV`)
    return false
  }

  fileToUpload.value = uploadFile.raw!
  uploadErrors.value = false

  return true
}

/**
 * Handles upload file remove.
 *
 * @param uploadFile the file removed.
 * @param uploadFiles the file list.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleRemove: UploadProps['onChange'] = async (uploadFile: UploadFile, uploadFiles: UploadFiles) => {
  fileToUpload.value = undefined
  uploadErrors.value = false
}

const submitUpload = () => {
  mutate({
    workOrderNumber: props.workOrderNumber,
    csvFile: fileToUpload.value!,
  })
}

const downloadSampleFile = async () => {
  try {
    await downloadFile(await downloadSampleBulkMinersFile(), 'bulk_miners_sample.csv')
  } catch (err: any) {
    ElMessage.error(`Failed to download sample file: ${err.message}`)
  }
}

const downloadFileErrors = async (errors: string[]) => {
  try {
    const textData = errors.join('\n')
    await downloadFile(new Blob([textData], { type: 'text/plain;charset=utf-8' }), 'upload_errors.txt')
  } catch (err: any) {
    ElMessage.error(`Failed to download errors file: ${err.message}`)
  }
}

/**
 * Handles dialog close. Cleans documents from upload component
 */
const handleDialogClose = async () => {
  fileToUpload.value = undefined
  uploadInst.value!.clearFiles()
  uploadErrors.value = false
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="500"
    title="Import Miners"
    :close-on-click-modal="!isPending"
    :close-on-press-escape="!isPending"
    :show-close="!isPending"
    @close="handleDialogClose"
  >
    <div class="layout">
      <p>
        The file must be a CSV that complies with the
        <el-link type="primary" @click="downloadSampleFile">Bulk Miners File Format</el-link>
      </p>
      <el-upload
        ref="uploadInst"
        drag
        :auto-upload="false"
        :limit="1"
        :on-exceed="handleExceed"
        :on-change="handleChange"
        :on-remove="handleRemove"
        :accept="supportefFileMimeTypesStr"
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
      </el-upload>
      <el-alert v-if="uploadErrors" :closable="false" type="error" title="Some errors were found in your file" />
      <el-button :disabled="fileToUpload === undefined" :loading="isPending" type="primary" @click="submitUpload"
        >Submit</el-button
      >
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-link {
  vertical-align: bottom;
}

.layout {
  gap: var(--spacing-spacing-md);
  display: flex;
  flex-direction: column;
}
</style>
